import { useState } from 'react';

import { Button, Group, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';

import { useAuth } from '../../../hooks/useAuth.js';
import type { EnvironmentalDataFormData, RotaryKiln } from '../../../types/reports.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';
import { SectionToggleRow } from '../../ui/SectionToggleRow.js';
import { createEmptyMeasurement } from './KilnForm.js';
import { KilnAnnualValuesForm } from './KilnAnnualValuesForm.js';
import { KilnMeasurementCard, measurementErrorSig } from './KilnMeasurementCard.js';
import { kilnTitle } from './kilnTitle.js';
import { bulkDeleteConfirmText, canBulkDelete } from './measurementBulkDelete.js';

type EnvForm = UseFormReturnType<EnvironmentalDataFormData>;

interface DrehofenSectionProps {
  form: EnvForm;
  disabled?: boolean;
  loading?: boolean;
  /**
   * 27.1-07 (D-11): Berichtsjahr fuer die Abschnittsueberschrift "Jahresmittelwerte {Jahr}" in
   * `renderKilnBody`, durchgereicht von `ReportEditPage` ueber `EnvironmentalDataForm`.
   */
  reportingPeriod?: string;
}

// Re-indiziert ein Set<number> nach dem Entfernen des Eintrags removedIdx: Eintraege darunter
// bleiben, Eintraege darueber ruecken um eins nach unten, der entfernte Eintrag faellt weg
// (Pitfall 5). Wird nur noch von der Messungs-Ebene gebraucht (Ofen-Ebene ist seit D-23
// ausschliesslich Anzeige, kein insertListItem/removeListItem mehr auf `rotaryKilns` selbst).
function reindexSet(set: Set<number>, removedIdx: number): Set<number> {
  const next = new Set<number>();
  set.forEach((idx) => {
    if (idx < removedIdx) next.add(idx);
    else if (idx > removedIdx) next.add(idx - 1);
  });
  return next;
}

export function DrehofenSection({
  form,
  disabled,
  loading,
  reportingPeriod,
}: DrehofenSectionProps) {
  const kilns = form.getValues().rotaryKilns ?? [];
  // `reportingPeriod` ist ein ISO-Datumsstring ("2026-01-01") -- die ersten vier Zeichen sind das
  // Jahr; kein Date-Objekt, kein Zeitzonenrisiko (D-11).
  const reportingYear = reportingPeriod?.slice(0, 4);
  // form.errors treibt die per-Messung-Fehlersignatur fuer KilnMeasurementCard (Plan 19-09);
  // einmal pro Render gelesen, analog `errors` in BrennstoffeSection.tsx.
  const errors = form.errors as Record<string, unknown>;
  // Plan 35-13 (D-16): Rollenabfrage fuer den Knopf "Bestehende Messungen löschen" --
  // `canBulkDelete` erlaubt ihn auch an Oefen ohne eigenes Anlegerecht, wenn die Verwaltungsrolle
  // vorliegt (der VDZ-Neuimport-Fall).
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;

  // Derselbe Zaehler PRO Ofen-Index fuer die Messungs-Ebene -- ein globaler Zaehler wuerde beim
  // Hinzufuegen einer Messung in Ofen A auch alle Messungen von Ofen B remounten (Pitfall 3).
  const [measurementsRev, setMeasurementsRev] = useState<Record<number, number>>({});

  // D-11: die Ofen-Ebene behaelt ihr heutiges Verhalten (initial alle offen); die
  // Messungs-Ebene startet dagegen zugeklappt (initial leeres Set je Ofen).
  const [expandedKilns, setExpandedKilns] = useState<Set<number>>(
    () => new Set(kilns.map((_, i) => i)),
  );
  const [expandedMeasurements, setExpandedMeasurements] = useState<Record<number, Set<number>>>({});

  function toggleKiln(idx: number): void {
    setExpandedKilns((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  function toggleMeasurement(kilnIdx: number, measurementIdx: number): void {
    setExpandedMeasurements((prev) => {
      const kilnSet = new Set(prev[kilnIdx] ?? []);
      if (kilnSet.has(measurementIdx)) kilnSet.delete(measurementIdx);
      else kilnSet.add(measurementIdx);
      return { ...prev, [kilnIdx]: kilnSet };
    });
  }

  // D-17: neue Messung wird IMMER frisch angelegt, niemals durch Kopieren einer bestehenden.
  function addMeasurement(kilnIdx: number): void {
    const currentMeasurements = kilns[kilnIdx]?.measurements ?? [];
    form.insertListItem(`rotaryKilns.${kilnIdx}.measurements`, createEmptyMeasurement());
    setExpandedMeasurements((prev) => {
      const kilnSet = new Set(prev[kilnIdx] ?? []);
      kilnSet.add(currentMeasurements.length);
      return { ...prev, [kilnIdx]: kilnSet };
    });
    setMeasurementsRev((prev) => ({ ...prev, [kilnIdx]: (prev[kilnIdx] ?? 0) + 1 }));
  }

  // D-15: Loeschen einer einzelnen Messung erfordert immer eine Bestaetigung, auch bei
  // vollstaendig leerer Messung.
  function confirmRemoveMeasurement(kilnIdx: number, measurementIdx: number): void {
    const kiln = kilns[kilnIdx];
    const kilnName = kiln?.kilnName || `Drehofen ${kilnIdx + 1}`;

    modals.openConfirmModal({
      title: 'Messung löschen',
      children: (
        <Text>
          Messung {measurementIdx + 1} von {kilnName} löschen?
        </Text>
      ),
      labels: { confirm: 'Löschen', cancel: 'Abbrechen' },
      confirmProps: { color: 'red' },
      onConfirm: () => removeMeasurement(kilnIdx, measurementIdx),
    });
  }

  // D-16: der Loeschbutton der letzten Messung bleibt aktiv -- ein Ofen darf ohne jede Messung
  // existieren.
  function removeMeasurement(kilnIdx: number, measurementIdx: number): void {
    form.removeListItem(`rotaryKilns.${kilnIdx}.measurements`, measurementIdx);
    setExpandedMeasurements((prev) => {
      const kilnSet = reindexSet(prev[kilnIdx] ?? new Set<number>(), measurementIdx);
      return { ...prev, [kilnIdx]: kilnSet };
    });
    setMeasurementsRev((prev) => ({ ...prev, [kilnIdx]: (prev[kilnIdx] ?? 0) + 1 }));
  }

  // Plan 35-13 (D-16, Sketch 020 Entscheidung 5): Sammelloeschung fuer einen Neuimport. Der
  // Knopf sitzt an der Ofenkarte, nicht im Import selbst -- hier sieht man, was man loescht.
  // Geschrieben wird nichts unmittelbar: `setFieldValue` aendert nur den Formularzustand, wirksam
  // wird das erst mit dem naechsten normalen Speichern der Meldung (dieselbe Zusage wie im ganzen
  // Formular). Kein eigener API-Aufruf, der Ofen selbst (Stammdaten, plantKiln) bleibt unberuehrt.
  function confirmBulkDeleteMeasurements(kilnIdx: number): void {
    const kiln = kilns[kilnIdx];
    const kilnName = kiln?.kilnName || `Drehofen ${kilnIdx + 1}`;
    const measurementCount = (kiln?.measurements ?? []).length;

    modals.openConfirmModal({
      title: 'Alle Messungen löschen',
      children: <Text>{bulkDeleteConfirmText({ kilnName, measurementCount })}</Text>,
      labels: { confirm: 'Löschen', cancel: 'Abbrechen' },
      confirmProps: { color: 'red' },
      onConfirm: () => removeAllMeasurements(kilnIdx),
    });
  }

  function removeAllMeasurements(kilnIdx: number): void {
    form.setFieldValue(`rotaryKilns.${kilnIdx}.measurements`, []);
    setExpandedMeasurements((prev) => ({ ...prev, [kilnIdx]: new Set<number>() }));
    // Derselbe Remount-Zaehler wie beim Loeschen einer einzelnen Messung (Pitfall 3) --
    // insert/removeListItem und setFieldValue bumpen formKey nicht selbst.
    setMeasurementsRev((prev) => ({ ...prev, [kilnIdx]: (prev[kilnIdx] ?? 0) + 1 }));
  }

  function renderKilnBody(kiln: RotaryKiln, kilnIdx: number) {
    const measurements = kiln.measurements ?? [];
    const kilnExpandedMeasurements = expandedMeasurements[kilnIdx] ?? new Set<number>();
    const rev = measurementsRev[kilnIdx] ?? 0;
    // Phase 28 (D-25/D-26): Vorbelegung `true`, damit Altzeilen ohne Stammdatenbezug (kein
    // plantKiln) sich verhalten wie vor dieser Phase -- unbeschraenktes Anlegen/Loeschen.
    const allowsOwnMeasurements = kiln.plantKiln?.allowsOwnMeasurements ?? true;

    return (
      <Stack gap="lg">
        {/* D-11 (Sketch 006, Variante A): oben die neun kontinuierlichen Jahresmittelwerte am
            Ofen, darunter unveraendert die bestehende Messungsliste -- zwei
            FormSubsection-Bloecke, keine dritte Akkordeon-Ebene. */}
        <KilnAnnualValuesForm form={form} kilnIdx={kilnIdx} disabled={disabled} />

        <FormSubsection title="Einzelmessungen">
          <Stack gap="md">
            {/* D-16: keine Messungen ist ein zulaessiger Zustand -- Hinweistext, keine Fehleranzeige.
                D-25: an einem Ofen ohne eigenes Anlegerecht verliert der Leertext seinen zweiten
                Satz, der auf den dort nicht existierenden Button verweisen wuerde (UI-SPEC Punkt 15). */}
            {measurements.length === 0 ? (
              <Text size="sm" c="vdzGray.6">
                {allowsOwnMeasurements
                  ? 'Noch keine Messungen erfasst. Klicken Sie auf "Messung hinzufügen", um die erste Messung anzulegen.'
                  : 'Noch keine Messungen erfasst.'}
              </Text>
            ) : (
              // D-12 (design/img_4.png): der Ofen ist selbst die graue Karte (SectionToggleRow
              // variant="card"), die Messungen sitzen darin als weisse Sub-Cards -- kein zweiter
              // grauer Hintergrund mehr, sonst grau-auf-grau.
              <Stack gap="xs">
                {measurements.map((_, measurementIdx) => (
                  // Key enthaelt measurementsRev[kilnIdx]: erzwingt Remount aller Messungs-Karten
                  // dieses Ofens bei Add/Remove (formKey wird von insert/removeListItem NICHT
                  // gebumpt, Pitfall 3), damit die richtige Karte verschwindet/erscheint.
                  <KilnMeasurementCard
                    key={`m-${rev}-${measurementIdx}`}
                    form={form}
                    kilnIdx={kilnIdx}
                    measurementIdx={measurementIdx}
                    expanded={kilnExpandedMeasurements.has(measurementIdx)}
                    onToggle={() => toggleMeasurement(kilnIdx, measurementIdx)}
                    // D-26: wer nicht anlegen darf, kann ein Loeschen nicht rueckgaengig machen --
                    // Anlegen und Loeschen gehoeren zum selben Recht. `undefined` statt einer
                    // Closure laesst KilnMeasurementCard den Loeschen-Knopf ganz weglassen.
                    onDelete={
                      allowsOwnMeasurements
                        ? () => confirmRemoveMeasurement(kilnIdx, measurementIdx)
                        : undefined
                    }
                    disabled={disabled}
                    errorSig={measurementErrorSig(
                      errors,
                      `rotaryKilns.${kilnIdx}.measurements.${measurementIdx}`,
                    )}
                  />
                ))}
              </Stack>
            )}

            {/* Phase 28 (D-25, UI-SPEC Punkt 15): am Ofen mit eigenem Anlegerecht bleibt der
                Button unveraendert; ohne das Recht steht an genau derselben Stelle der Hinweis auf
                den Emissionsbericht -- ausdruecklich KEIN Merkmal in der Kopfzeile der Ofenkarte
                (Sketch 006, Variante A). */}
            {allowsOwnMeasurements ? (
              <Button
                leftSection={<IconPlus size={16} />}
                variant="light"
                onClick={() => addMeasurement(kilnIdx)}
                disabled={disabled}
              >
                Messung hinzufügen
              </Button>
            ) : (
              <Group gap="xs" wrap="nowrap">
                <IconInfoCircle size={16} color="var(--mantine-color-vdzGray-6)" />
                <Text size="sm" c="vdzGray.6">
                  Die Einzelmessungen an diesem Ofen ergänzt der VDZ aus dem Emissionsbericht.
                </Text>
              </Group>
            )}

            {/* Plan 35-13 (D-16): fuer einen Neuimport muessen vorhandene Messungen zuerst
                weg -- der Emissionsbericht-Import ergaenzt nur, er ersetzt nie (Sketch 020
                Entscheidung 5). Gedaempft, damit er neben "Messung hinzufügen" nicht wie die
                Hauptaktion wirkt. */}
            {canBulkDelete({
              measurementCount: measurements.length,
              allowsOwnMeasurements,
              isAdmin,
            }) && (
              <Button
                leftSection={<IconTrash size={16} />}
                variant="subtle"
                color="red"
                onClick={() => confirmBulkDeleteMeasurements(kilnIdx)}
                disabled={disabled}
              >
                Bestehende Messungen löschen
              </Button>
            )}
          </Stack>
        </FormSubsection>
      </Stack>
    );
  }

  if (loading) {
    return <Text c="vdzGray.6">Laden...</Text>;
  }

  // Der Tab-Inhalt liegt bereits in der gemeinsamen Tab-Karte (EnvironmentalDataForm) -- daher
  // keine eigene FormSection-Karte mehr, sondern eine FormSubsection wie die uebrigen
  // Umweltdaten-Tabs: Titel + Beschreibung als Tab-Ueberschrift. Phase 28 (D-23): kein
  // `rightSection`-Button mehr -- Anlegen/Entfernen eines Drehofens gibt es im Formular nicht mehr.
  return (
    <FormSubsection
      title="Drehöfen"
      description="Erfassen Sie Emissionsdaten für jeden Drehofen Ihres Werks."
    >
      {/* Liste = ein Feld (D-12): eine Beschriftung an der Abschnittswurzel. */}
      <RegisterFieldTab field="rotaryKilns" label="Drehöfen" />
      <Stack gap="md">
        {kilns.length === 0 ? (
          // Phase 28 (D-24, UI-SPEC Punkt 11): Werke ohne Ofen behalten den Reiter, kein
          // dynamisches Ausblenden -- nur ein ruhiger Hinweis statt der Ofenliste, kein Icon,
          // keine Alert-Box.
          <Text size="sm" c="vdzGray.6">
            Für dieses Werk sind keine Drehöfen hinterlegt. Sollte das nicht stimmen, bitte beim
            VDZ melden.
          </Text>
        ) : (
          <>
            {kilns.map((kiln, idx) => {
              const measurementCount = (kiln.measurements ?? []).length;
              return (
                <SectionToggleRow
                  // Phase 28 (D-23): kein Versionszaehler mehr im Key -- die Ofen-Ebene traegt kein
                  // insertListItem/removeListItem mehr, ein Zaehler zum Erzwingen des Remounts ist
                  // damit gegenstandslos (D-27/CR-01: die einzigen beiden Schreibstellen dieses
                  // Zaehlers verschwinden hier ersatzlos).
                  key={`k-${idx}`}
                  variant="card"
                  title={kilnTitle(kiln, idx, reportingYear)}
                  meta={`${measurementCount} ${measurementCount === 1 ? 'Messung' : 'Messungen'}`}
                  expanded={expandedKilns.has(idx)}
                  onToggle={() => toggleKiln(idx)}
                >
                  {renderKilnBody(kiln, idx)}
                </SectionToggleRow>
              );
            })}

            {/* Phase 28 (D-23, UI-SPEC Punkt 12): Ersatz fuer die entfallenen
                "Drehofen hinzufuegen"/"Drehofen entfernen"-Buttons, unterhalb aller Ofenkarten. */}
            <Text
              size="xs"
              c="vdzGray.6"
              style={{
                paddingLeft: 10,
                borderLeft: '2px solid var(--mantine-color-vdzGray-3)',
                marginTop: 14,
              }}
            >
              Ist ein Drehofen weggefallen oder ein neuer hinzugekommen? Bitte beim VDZ melden.
            </Text>
          </>
        )}
      </Stack>
    </FormSubsection>
  );
}
